import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectCompanyInfo } from "store/companySlice";
import {
  fetchOnboarding,
  selectCompletedCount,
  selectIsAllCompleted,
  selectOnboarding,
  setDismissed,
  toggleExpanded,
  toggleTaskExpanded,
  updateTask,
} from "store/onboardingSlice";

import { ReactComponent as CheckmarkSvg } from "assets/icons/checkmark/checkmark.svg";
import { ReactComponent as ChevronDownSvg } from "assets/icons/chevron/Down.svg";
import Icon from "layout/atoms/Icon";

import task1 from "assets/onboarding/task1.webm";
import task2 from "assets/onboarding/task2.webm";
import task3 from "assets/onboarding/task3.webm";
import task4 from "assets/onboarding/task4.webm";

import {
  Checkbox,
  ChevronIcon,
  CompletedMessage,
  Content,
  DismissButton,
  Footer,
  Header,
  HeaderLeft,
  HeaderRight,
  LearnMoreLink,
  MarkDoneButton,
  PlaceholderBox,
  ProgressBadge,
  ProgressBar,
  RocketIcon,
  TaskContent,
  TaskDescription,
  TaskGifPlaceholder,
  TaskItemContent,
  TaskItemHeader,
  TaskItemWrapper,
  TaskList,
  TaskTitle,
  Title,
  Wrapper,
} from "./styles";

const TASK_CONFIG = {
  "create-board": {
    media: task1,
  },
  "add-idea": {
    media: task2,
  },
  "update-idea-status": {
    media: task3,
  },
  "configure-widget": {
    media: task4,
    learnMoreUrl:
      "https://ideolo.co/blog/how-to-set-up-ideolo-widget-on-your-website",
  },
};

const OnboardingChecklist = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { taskIds, doneTasks, dismissed, status, isExpanded, expandedTaskId } =
    useSelector(selectOnboarding);
  const completedCount = useSelector(selectCompletedCount);
  const isAllCompleted = useSelector(selectIsAllCompleted);
  const company = useSelector(selectCompanyInfo);

  useEffect(() => {
    if (status === "idle" && company.id) {
      dispatch(fetchOnboarding());
    }
  }, [status, company.id, dispatch]);

  const isAdminPage = location.pathname.startsWith("/admin/");

  if (!isAdminPage || status !== "succeeded" || dismissed) {
    return null;
  }

  const progress = (completedCount / taskIds.length) * 100;

  const handleToggle = () => {
    dispatch(toggleExpanded());
  };

  const handleDismiss = () => {
    dispatch(setDismissed(true));
  };

  const handleTaskClick = (taskId) => {
    dispatch(toggleTaskExpanded(taskId));
  };

  const handleMarkDone = (e, taskId) => {
    e.stopPropagation();
    dispatch(updateTask({ taskId, done: true }));

    const currentIndex = taskIds.indexOf(taskId);
    const newDoneTasks = [...doneTasks, taskId];

    const nextTaskId = taskIds[currentIndex + 1];
    if (nextTaskId && !newDoneTasks.includes(nextTaskId)) {
      dispatch(toggleTaskExpanded(nextTaskId));
    } else {
      const firstIncomplete = taskIds.find((id) => !newDoneTasks.includes(id));
      if (firstIncomplete) {
        dispatch(toggleTaskExpanded(firstIncomplete));
      }
    }
  };

  const isTaskDone = (taskId) => doneTasks.includes(taskId);

  return (
    <Wrapper>
      <Header onClick={handleToggle}>
        <HeaderLeft>
          <RocketIcon>✨</RocketIcon>
          <Title>{t("onboarding.title")}</Title>
        </HeaderLeft>
        <HeaderRight>
          <ProgressBadge>
            {completedCount}/{taskIds.length}
          </ProgressBadge>
          <ChevronIcon $isExpanded={isExpanded}>
            <Icon src={ChevronDownSvg} size="16px" />
          </ChevronIcon>
        </HeaderRight>
      </Header>

      <Content $isExpanded={isExpanded}>
        <ProgressBar $progress={progress}>
          <div />
        </ProgressBar>

        {isAllCompleted ? (
          <CompletedMessage>
            <h4>{t("onboarding.completedTitle")}</h4>
            <p>{t("onboarding.completedMessage")}</p>
            <MarkDoneButton onClick={handleDismiss}>
              {t("onboarding.finish")}
            </MarkDoneButton>
          </CompletedMessage>
        ) : (
          <TaskList>
            {taskIds.map((taskId) => {
              const completed = isTaskDone(taskId);
              return (
                <TaskItemWrapper key={taskId}>
                  <TaskItemHeader
                    $completed={completed}
                    onClick={() => handleTaskClick(taskId)}
                  >
                    <Checkbox $completed={completed}>
                      {completed && <Icon src={CheckmarkSvg} size="12px" />}
                    </Checkbox>
                    <TaskContent>
                      <TaskTitle $completed={completed}>
                        {t(`onboarding.tasks.${taskId}`)}
                      </TaskTitle>
                    </TaskContent>
                  </TaskItemHeader>
                  <TaskItemContent $isExpanded={expandedTaskId === taskId}>
                    <TaskGifPlaceholder>
                      {TASK_CONFIG[taskId]?.media ? (
                        <video
                          src={TASK_CONFIG[taskId].media}
                          autoPlay
                          loop
                          muted
                          playsInline
                        />
                      ) : (
                        <PlaceholderBox>{taskId}</PlaceholderBox>
                      )}
                      <TaskDescription>
                        {t(`onboarding.descriptions.${taskId}`)}
                        {TASK_CONFIG[taskId]?.learnMoreUrl && (
                          <LearnMoreLink
                            href={TASK_CONFIG[taskId].learnMoreUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {t("onboarding.learnMore")}
                          </LearnMoreLink>
                        )}
                      </TaskDescription>
                      <MarkDoneButton
                        onClick={(e) => handleMarkDone(e, taskId)}
                        disabled={completed}
                      >
                        {completed
                          ? t("onboarding.done")
                          : t("onboarding.markDone")}
                      </MarkDoneButton>
                    </TaskGifPlaceholder>
                  </TaskItemContent>
                </TaskItemWrapper>
              );
            })}
          </TaskList>
        )}

        <Footer>
          <DismissButton onClick={handleDismiss}>
            {t("onboarding.dismiss")}
          </DismissButton>
        </Footer>
      </Content>
    </Wrapper>
  );
};

export default OnboardingChecklist;
